import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft, Sparkle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Knowledge Ticker — silent, calm marquee showing the latest published
 * Knowledge Center articles + a subtle progress indicator underneath.
 * If no articles are published yet, renders nothing (no clutter on the homepage).
 */
const KnowledgeTicker = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, categories: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, l, c] = await Promise.all([
          fetch(`${API}/api/knowledge/articles/ticker?limit=8`).then((r) => r.json()),
          fetch(`${API}/api/knowledge/articles?page_size=1`).then((r) => r.json()),
          fetch(`${API}/api/knowledge/categories`).then((r) => r.json()),
        ]);
        if (cancelled) return;
        setItems(t.items || []);
        setStats({
          total: l.total || 0,
          categories: (c.categories || []).length,
        });
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!items.length) return null;

  // Duplicate list to create a seamless loop
  const loop = [...items, ...items];

  return (
    <section
      data-testid="knowledge-ticker"
      dir="rtl"
      aria-label="مركز المعرفة"
    >
      {/* Ticker bar */}
      <div className="bg-[#1F3D2B] text-white py-3 border-y border-[#2A4F38]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center gap-4">
          <Link
            to="/knowledge"
            className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.2em] uppercase text-[#E6C97A] hover:text-white transition-colors"
          >
            <BookOpen size={13} weight="fill" />
            مركز المعرفة
          </Link>
          <span className="hidden sm:block h-4 w-px bg-white/20 shrink-0" />
          <div className="flex-1 overflow-hidden relative">
            <div className="kc-ticker-track flex gap-8 whitespace-nowrap">
              {loop.map((a, i) => (
                <Link
                  key={`${a.slug}-${i}`}
                  to={`/knowledge/${a.slug}`}
                  className="inline-flex items-center gap-2 text-[13px] text-[#EDEAE1] hover:text-white transition-colors shrink-0"
                >
                  {a.category_name && (
                    <span className="text-[11px] text-[#E6C97A]/70">
                      {a.category_name} ·
                    </span>
                  )}
                  <span>{a.title}</span>
                  <ArrowLeft size={11} className="rtl-flip opacity-60" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator strip — soft, warm, editorial */}
      <div
        data-testid="knowledge-progress"
        className="bg-[#F5F2EC] border-b border-[#E4D9C4] py-2.5"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-center sm:justify-between gap-x-6 gap-y-1 text-[11.5px] sm:text-[12px] text-[#8B6F3E]">
          <span className="inline-flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" className="text-[#B08A50]" />
            بحث تربوي مستمر
          </span>
          <span className="hidden sm:inline-block h-3 w-px bg-[#D9CFB8]" />
          <span>
            <strong className="text-[#1F3D2B]">{stats.total}</strong>{' '}
            {stats.total === 1 ? 'مقال مرجعي' : 'مقالات مرجعية'}
          </span>
          <span className="hidden sm:inline-block h-3 w-px bg-[#D9CFB8]" />
          <span>
            <strong className="text-[#1F3D2B]">{stats.categories}</strong>{' '}
            تصنيفات متخصصة
          </span>
          <span className="hidden sm:inline-block h-3 w-px bg-[#D9CFB8]" />
          <Link
            to="/knowledge"
            data-testid="knowledge-progress-cta"
            className="text-[#1F3D2B] font-bold border-b border-[#B08A50] hover:border-[#1F3D2B] pb-0.5 transition-colors"
          >
            تصفّح المركز →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default KnowledgeTicker;
